import { Box, Divider, FormField, RadioGroup, Text } from '@wix/design-system';
import React, { useContext } from 'react';
import { RicosContext } from 'ricos-context';
import type { BICallbacks } from 'ricos-types';
import { LTRIcon, RTLIcon } from 'wix-rich-content-plugin-commons';
import { ThumbnailPicker, ToggleButton } from 'wix-rich-content-ui-components';

import classes from '../../../statics/styles/collapsible-list-settings.scss';
import {
  COLLAPSIBLE_LIST_EXPANDED_DEFAULT,
  COLLAPSIBLE_LIST_ONE_PAIR_EXPANDED,
} from '../../dataHooks';
import type { SettingsState } from '../../modals/SettingsModals';
import type { InitialExpandedItems, ListDirection } from '../../types';
import { COLLAPSIBLE_LIST_TYPE, initialExpandedItems, listDirection } from '../../types';

type Props = {
  value: SettingsState;
  onChange: (value: SettingsState) => void;
  pluginEvents: BICallbacks;
  isQapageDataEnabled: boolean;
};

export const CollapsibleListSettings: React.FC<Props> = ({
  onChange,
  value,
  pluginEvents,
  isQapageDataEnabled,
}) => {
  const { t } = useContext(RicosContext);

  const ExpandOptions: React.FC = () => (
    <>
      <FormField
        label={t('CollapsibleList_CollapsibleListSettings_Tab_Settings_CollapseView_Title')}
      >
        <RadioGroup
          value={value.initialExpanded.type}
          onChange={(changedValue: InitialExpandedItems) => {
            const newValue =
              changedValue === 'ALL'
                ? {
                    initialExpanded: { type: 'ALL' as const },
                  }
                : {
                    initialExpanded: {
                      type: changedValue,
                      expandOnlyOne:
                        value.initialExpanded.type !== 'ALL'
                          ? value.initialExpanded.expandOnlyOne
                          : false,
                    },
                  };
            onChange({ ...value, ...newValue });
          }}
        >
          <RadioGroup.Radio
            value={initialExpandedItems.ALL}
            dataHook={COLLAPSIBLE_LIST_EXPANDED_DEFAULT.ALL}
          >
            <Text size="small">
              {t('CollapsibleList_CollapsibleListSettings_Tab_Settings_CollapseView_Expanded')}
            </Text>
          </RadioGroup.Radio>
          <RadioGroup.Radio
            value={initialExpandedItems.FIRST}
            dataHook={COLLAPSIBLE_LIST_EXPANDED_DEFAULT.FIRST}
          >
            <Text size="small">
              {t('CollapsibleList_CollapsibleListSettings_Tab_Settings_CollapseView_FirstExpanded')}
            </Text>
          </RadioGroup.Radio>
          <RadioGroup.Radio
            value={initialExpandedItems.NONE}
            dataHook={COLLAPSIBLE_LIST_EXPANDED_DEFAULT.NONE}
          >
            <Text size="small">
              {t('CollapsibleList_CollapsibleListSettings_Tab_Settings_CollapseView_Collapsed')}
            </Text>
          </RadioGroup.Radio>
        </RadioGroup>
      </FormField>
      {value.initialExpanded.type !== initialExpandedItems.ALL && (
        <ToggleButton
          checked={!!value.initialExpanded.expandOnlyOne}
          title={t('CollapsibleList_CollapsibleListSettings_Tab_Settings_CollapseView_InSections')}
          dataHook={COLLAPSIBLE_LIST_ONE_PAIR_EXPANDED}
          onChange={() => {
            const expandOnlyOne =
              value.initialExpanded.type === 'ALL' ? false : value.initialExpanded.expandOnlyOne;
            onChange({
              ...value,
              initialExpanded: {
                type: value.initialExpanded.type,
                expandOnlyOne: !expandOnlyOne,
              },
            });
          }}
        />
      )}
    </>
  );

  const dataSource = [
    {
      value: listDirection.LTR,
      label: t('CollapsibleList_CollapsibleListSettings_Tab_Settings_Direction_LTR'),
      icon: LTRIcon,
      dataHook: 'ltrDirection',
    },
    {
      value: listDirection.RTL,
      label: t('CollapsibleList_CollapsibleListSettings_Tab_Settings_Direction_RTL'),
      icon: RTLIcon,
      dataHook: 'rtlDirection',
    },
  ];

  const DirectionOptions: React.FC = () => (
    <>
      <ThumbnailPicker
        dataSource={dataSource.map(item => ({
          value: item.value as ListDirection,
          dataHook: item.dataHook,
          renderBackgroundImage: props => <item.icon {...props} />,
          label: item.label,
        }))}
        selectedValue={value.direction}
        title={t('CollapsibleList_CollapsibleListSettings_Tab_Settings_Direction_Title')}
        titleInfoContent={t(
          'CollapsibleList_CollapsibleListSettings_Tab_Settings_Direction_Title_Tooltip'
        )}
        onChange={(direction: ListDirection) => {
          onChange({ ...value, direction });
        }}
      />
    </>
  );

  const QaPageToggle: React.FC = () => {
    const onToggle = () => {
      pluginEvents?.onChangePluginSettings?.({
        pluginId: COLLAPSIBLE_LIST_TYPE,
        actionName: 'QapageData',
        value: value.isQapageData ? 'no' : 'yes',
      });

      onChange({ ...value, isQapageData: !value.isQapageData });
    };

    return (
      <ToggleButton
        checked={value.isQapageData}
        title={t('CollapsibleList_CollapsibleListSettings_Tab_Settings_FAQ_Enabled_Toggle')}
        dataHook={'isQapageDataToggle'}
        onChange={onToggle}
        infoContent={
          <>
            {t('CollapsibleList_CollapsibleListSettings_Tab_Settings_FAQ_Enabled_Tooltip')}{' '}
            <a
              href="https://wix.wixanswers.com/app/kb/article/a5b1247b-baf3-4baa-a7f1-7137e10ca768/en/"
              target="_blank"
              rel="noreferrer"
              className={classes.link}
            >
              {t('LinkPanel_Learn_More_Label')}
            </a>
          </>
        }
      />
    );
  };

  return (
    <Box direction="vertical" gap="SP3">
      <ExpandOptions />
      {isQapageDataEnabled && (
        <>
          <Divider />
          <QaPageToggle />
        </>
      )}
      <Divider />
      <DirectionOptions />
    </Box>
  );
};
